import {useEffect, useRef} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isNil, equals} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  CreateMinisaleApiArg,
  interestApi,
  MinisaleResponseBody,
  UpdateMinisaleApiArg,
  useChangeMinisaleCostReasonMutation,
  useChangeMinisaleExpectedCostsMutation,
  useChangeMinisalePriceOfferedMutation,
  useChangeMinisalePriceRequestedMutation,
  useChangeMinisaleReasonMutation,
  useChangeMinisaleVatDeductibleMutation,
  useCreateMinisaleMutation,
  useUpdateMinisaleMutation,
} from '@dms/api/finance';
import {ApiError} from '@dms/api/shared';
import {
  handleApiError,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
  useTenant,
} from '@dms/shared';

import {getSafely} from 'shared/web';

import {useInterestData} from '../../../hooks/useInterestData';
import {BuyingFormType} from '../types/BuyingFormType';
import {transformBuyingFormToMinisale} from '../utils/transformBuyingFormToMinisale';

export function useMinisaleUpdate(interestId: string) {
  const dispatch = useDispatch();
  const store = useStore();
  const {tenantCurrency} = useTenant();
  const pendingValuesRef = useRef<BuyingFormType | null>(null);

  const {miniSale, isLoading} = useInterestData(interestId);

  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {handleMandatoryFieldsError, isMandatoryFieldsError} = useMandatoryFieldsErrorDialog();

  // fixedCacheKey stores mutation state in the Redux store under a stable key,
  // allowing us to check if a mutation is in-flight via store.getState() — immune to stale closures
  // caused by AutoSave's debounced callbacks firing from old render cycles.
  const createMinisaleCacheKey = `create-minisale-${interestId}`;
  const updateMinisaleCacheKey = `update-minisale-${interestId}`;
  const changeVatDeductibleCacheKey = `change-minisale-vat-deductible-${interestId}`;
  const changeCostReasonCacheKey = `change-minisale-cost-reason-${interestId}`;
  const changeExpectedCostsCacheKey = `change-minisale-expected-costs-${interestId}`;
  const changePriceOfferedCacheKey = `change-minisale-price-offered-${interestId}`;
  const changePriceRequestedCacheKey = `change-minisale-price-requested-${interestId}`;
  const changeReasonCacheKey = `change-minisale-reason-${interestId}`;

  const [createMinisale] = useCreateMinisaleMutation({
    fixedCacheKey: createMinisaleCacheKey,
  });
  const [updateMiniSale] = useUpdateMinisaleMutation({
    fixedCacheKey: updateMinisaleCacheKey,
  });

  const [changeMinisaleCostReason] = useChangeMinisaleCostReasonMutation({
    fixedCacheKey: changeCostReasonCacheKey,
  });
  const [changeMinisaleExpectedCosts] = useChangeMinisaleExpectedCostsMutation({
    fixedCacheKey: changeExpectedCostsCacheKey,
  });
  const [changeMinisalePriceOffered] = useChangeMinisalePriceOfferedMutation({
    fixedCacheKey: changePriceOfferedCacheKey,
  });
  const [changeMinisalePriceRequested] = useChangeMinisalePriceRequestedMutation({
    fixedCacheKey: changePriceRequestedCacheKey,
  });
  const [changeMinisaleReason] = useChangeMinisaleReasonMutation({
    fixedCacheKey: changeReasonCacheKey,
  });
  const [changeMinisaleVatDeductible] = useChangeMinisaleVatDeductibleMutation({
    fixedCacheKey: changeVatDeductibleCacheKey,
  });

  // Reads create mutation status directly from the Redux store at call time.
  // We cannot use useRef or closure-captured state because AutoSave's useDebouncedCallback
  // fires callbacks from stale closures (old render cycles), making refs unreliable.
  // Also checks 'fulfilled' status to guard the window between POST success and miniSale
  // query refetch completing — during which miniSale is still null.
  // Note: 'fulfilled' stays in the store indefinitely, but this is safe because once miniSale
  // becomes available, onBuyingUpdate always takes the UPDATE path and this check is never reached.
  const isCreateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, createMinisaleCacheKey);
    return mutationState?.status === 'pending' || mutationState?.status === 'fulfilled';
  };

  // Reads update mutation statuses directly from the Redux store at call time.
  // Prevents concurrent PUT/PATCH requests that would cause data loss — when any update is in-flight,
  // new values are queued in pendingValuesRef and flushed after all updates complete.
  const isAnyUpdateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const updateCacheKeys = [
      updateMinisaleCacheKey,
      changeVatDeductibleCacheKey,
      changeCostReasonCacheKey,
      changeExpectedCostsCacheKey,
      changePriceOfferedCacheKey,
      changePriceRequestedCacheKey,
      changeReasonCacheKey,
    ];
    return updateCacheKeys.some((key) => getSafely(mutations, key)?.status === 'pending');
  };

  const onBuyingUpdate = (values: BuyingFormType) => {
    const valuesWithCurrentUser: BuyingFormType = {...values, pricedBy: currentUser?.id};

    if (!miniSale || !miniSale.id) {
      if (isCreateInProgress()) {
        pendingValuesRef.current = {
          ...pendingValuesRef.current,
          ...valuesWithCurrentUser,
        };
        return;
      }

      const minisaleRequestBody = transformBuyingFormToMinisale(valuesWithCurrentUser);
      const arg: CreateMinisaleApiArg = {
        createMinisaleRequestBody: {
          interestId,
          ...minisaleRequestBody,
        },
      };
      createMinisale(arg)
        .unwrap()
        .then(() => {
          dispatch(interestApi.util.invalidateTags([{type: 'Interest', id: interestId}]));
        })
        .catch(handleApiError);
      return;
    }

    if (isAnyUpdateInProgress()) {
      pendingValuesRef.current = {
        ...pendingValuesRef.current,
        ...valuesWithCurrentUser,
      };
      return;
    }

    // Track which fields have changed to avoid unnecessary API calls
    const changes = {
      vatDeductible: hasVatDeductibleChanged(valuesWithCurrentUser, miniSale),
      costReason: hasCostReasonChanged(valuesWithCurrentUser, miniSale),
      expectedCosts: hasExpectedCostsChanged(valuesWithCurrentUser, miniSale),
      priceOffered: hasPriceOfferedChanged(valuesWithCurrentUser, miniSale),
      priceRequested: hasPriceRequestedChanged(valuesWithCurrentUser, miniSale),
      reason: hasReasonChanged(valuesWithCurrentUser, miniSale),
      otherFields: hasOtherFieldsChanged(valuesWithCurrentUser, miniSale),
      pricer: valuesWithCurrentUser.pricedBy !== miniSale.pricedBy,
    };

    // Create a promise array for all pending updates
    const pendingUpdates: Promise<any>[] = [];

    // Only update fields that actually changed
    if (changes.vatDeductible) {
      pendingUpdates.push(
        changeMinisaleVatDeductible({
          interestId,
          minisaleId: miniSale.id,
          changeMinisaleVatDeductibleRequestBody: {
            isVatDeductible: valuesWithCurrentUser.isVatDeductible?.[0] === 'true',
          },
        }).unwrap()
      );
    }

    if (changes.costReason) {
      pendingUpdates.push(
        changeMinisaleCostReason({
          interestId,
          minisaleId: miniSale.id,
          changeMinisaleCostReasonRequestBody: {
            costReason: valuesWithCurrentUser.costReason ?? null,
          },
        }).unwrap()
      );
    }

    if (changes.expectedCosts) {
      const amount = valuesWithCurrentUser.expectedCosts?.amount;
      const expectedCosts = isNil(amount)
        ? null
        : {
            amount: String(amount),
            currency:
              valuesWithCurrentUser.expectedCosts?.currency ??
              miniSale.expectedCosts?.currency ??
              miniSale.priceOfferedWithVat?.currency ??
              tenantCurrency ??
              '',
          };

      pendingUpdates.push(
        changeMinisaleExpectedCosts({
          interestId,
          minisaleId: miniSale.id,
          changeMinisaleExpectedCostsRequestBody: {
            expectedCosts,
          },
        }).unwrap()
      );
    }

    if (changes.priceOffered) {
      const amount = valuesWithCurrentUser.priceOfferedWithoutVat?.amount;
      const priceOffered = isNil(amount)
        ? null
        : {
            amount: String(amount),
            currency:
              valuesWithCurrentUser.priceOfferedWithoutVat?.currency ??
              miniSale.priceOfferedWithoutVat?.currency ??
              miniSale.priceOfferedWithVat?.currency ??
              tenantCurrency ??
              '',
          };

      pendingUpdates.push(
        changeMinisalePriceOffered({
          interestId,
          minisaleId: miniSale.id,
          changeMinisalePriceOfferedRequestBody: {
            priceOffered,
          },
        }).unwrap()
      );
    }

    if (changes.priceRequested) {
      const amount = valuesWithCurrentUser.priceRequested?.amount;
      const priceRequested = isNil(amount)
        ? null
        : {
            amount: String(amount),
            currency:
              valuesWithCurrentUser.priceRequested?.currency ??
              miniSale.priceRequested?.currency ??
              miniSale.priceOfferedWithVat?.currency ??
              tenantCurrency ??
              '',
          };

      pendingUpdates.push(
        changeMinisalePriceRequested({
          interestId,
          minisaleId: miniSale.id,
          changeMinisalePriceRequestedRequestBody: {
            priceRequested,
          },
        }).unwrap()
      );
    }

    if (changes.reason) {
      pendingUpdates.push(
        changeMinisaleReason({
          interestId,
          minisaleId: miniSale.id,
          changeMinisaleReasonRequestBody: {
            reason: valuesWithCurrentUser.reason ?? null,
          },
        }).unwrap()
      );
    }

    if (changes.otherFields || changes.pricer) {
      const minisaleRequestBody = transformBuyingFormToMinisale(valuesWithCurrentUser);
      const arg: UpdateMinisaleApiArg = {
        validateMandatoryFields: isMandatoryFieldsEnabled(undefined),
        updateMinisaleRequestBody: {
          interestId,
          ...minisaleRequestBody,
          minisaleId: miniSale.id,
        },
      };
      pendingUpdates.push(
        updateMiniSale(arg)
          .unwrap()
          .then(() => {
            if (shouldFetchMinisale(valuesWithCurrentUser, miniSale)) {
              dispatch(interestApi.util.invalidateTags([{type: 'miniSale', id: miniSale.id}]));
            }
          })
      );
    }

    if (pendingUpdates.length === 0) {
      return;
    }

    // Process all pending updates
    Promise.all(pendingUpdates)
      .catch((error: ApiError) => {
        if (isMandatoryFieldsError(error)) {
          return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
        }

        return handleApiError(error);
      })
      .finally(() => {
        // Flush values queued while mutations were in-flight.
        // Change detection runs against the closure's miniSale (pre-mutation state),
        // which may produce redundant but harmless API calls for already-saved fields.
        if (pendingValuesRef.current) {
          const pending = pendingValuesRef.current;
          pendingValuesRef.current = null;
          onBuyingUpdate(pending);
        }
      });
  };

  // Flush pending values after miniSale becomes available (POST completed + interest refetched)
  useEffect(() => {
    if (!miniSale?.id) {
      return;
    }

    if (pendingValuesRef.current) {
      const pendingValues = pendingValuesRef.current;
      pendingValuesRef.current = null;
      onBuyingUpdate(pendingValues);
    }
    // onBuyingUpdate intentionally omitted: including it would cause an infinite loop since
    // it depends on miniSale, which changes when this effect flushes pending values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniSale?.id]);

  return {
    onBuyingUpdate,
  };
}

const hasVatDeductibleChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) => {
  const newValue = values.isVatDeductible?.[0] === 'true';
  return newValue !== miniSale.isVatDeductible;
};

const hasCostReasonChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) =>
  values.costReason !== miniSale.costReason;

const moneyValueChanged = (
  newValue: {amount?: string; currency?: string} | null | undefined,
  oldValue: {amount?: string; currency?: string} | null | undefined
) => {
  const newAmount = newValue?.amount;
  const oldAmount = oldValue?.amount;

  if (isNil(newAmount) && isNil(oldAmount)) {
    return false;
  }

  // One amount exists and the other doesn't - change
  if ((isNil(newAmount) && !isNil(oldAmount)) || (!isNil(newAmount) && isNil(oldAmount))) {
    return true;
  }

  // Compare amounts and currencies
  return newAmount !== oldAmount || (newValue?.currency ?? '') !== (oldValue?.currency ?? '');
};

const hasExpectedCostsChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) =>
  moneyValueChanged(values.expectedCosts, miniSale.expectedCosts);

const hasPriceRequestedChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) =>
  moneyValueChanged(values.priceRequested, miniSale.priceRequested);

const hasPriceOfferedChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) =>
  moneyValueChanged(values.priceOfferedWithVat, miniSale.priceOfferedWithVat) ||
  moneyValueChanged(values.priceOfferedWithoutVat, miniSale.priceOfferedWithoutVat) ||
  moneyValueChanged(values.priceOfferedBase, miniSale.priceOfferedBase);

const hasReasonChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) =>
  values.reason !== miniSale.reason;

const hasOtherFieldsChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) => {
  const vehicleDataChanged = hasVehicleDataFieldsChanged(values, miniSale);
  if (vehicleDataChanged) {
    return true;
  }

  // Check additional fields that aren't checked by other functions
  if (values.customerBuyingStatement !== miniSale.customerBuyingStatement) {
    return true;
  }

  if (values.interestRate !== miniSale.interestRate) {
    return true;
  }

  if (values.vatType !== miniSale.vatType) {
    return true;
  }

  if (values.vatDirection !== miniSale.vatDirection) {
    return true;
  }

  return false;
};

const hasVehicleDataFieldsChanged = (values: BuyingFormType, miniSale: MinisaleResponseBody) => {
  const transformedValues = transformBuyingFormToMinisale(values);
  return !equals(transformedValues.vehicleData, miniSale.vehicleData);
};

export const shouldFetchMinisale = (
  values: BuyingFormType,
  miniSale: MinisaleResponseBody | null
) => {
  // If pricer changed, we should fetch
  if (values.pricedBy !== miniSale?.pricedBy) {
    return true;
  }

  // Check for changes in make and modelFamily
  const newMake = values.vehicleData?.make;
  const newModelFamily = values.vehicleData?.modelFamily;
  const oldMake = miniSale?.vehicleData?.make;
  const oldModelFamily = miniSale?.vehicleData?.modelFamily;

  // If both are defined in new values but not in old, fetch
  if (newMake && newModelFamily && (!oldMake || !oldModelFamily)) {
    return true;
  }

  // If both are defined in old values but not in new, fetch
  if (oldMake && oldModelFamily && (!newMake || !newModelFamily)) {
    return true;
  }

  return false;
};
